import {ActionList, ActionMenu, Button, Text} from '@primer/react'
import {Fragment} from 'react'

import {COPY, MANUFACTURER_ICONS, MODEL_PICKER_DATA} from './ModelPickerV2.data'
import Styles from './ModelPickerV2.module.css'
import type {ModelPickerItem, ModelPickerItemGroup} from './ModelPickerV2.types'

export interface ModelPickerV2Props {
  isOpen?: boolean
  onOpenChange?: (isOpen: boolean) => void
  currentModelId?: string | null
  itemGroups: ModelPickerItemGroup[]
  onModelClick: (modelId: string) => void
  canSelectModels?: boolean
  onSignInClick?: () => void
  isFreeModelsOnly?: boolean
}

export const defaultProps: Partial<ModelPickerV2Props> = {
  currentModelId: 'gpt-4.1',
  canSelectModels: true,
}

const getModelData = (modelId: string): ModelPickerItem | null => {
  for (const group of MODEL_PICKER_DATA) {
    for (const item of group.items) {
      if (item.id === modelId) {
        return item
      }
    }
  }
  return null
}

const ModelPickerV2 = (props: ModelPickerV2Props) => {
  const initializedProps: ModelPickerV2Props = {...defaultProps, ...props}
  const {
    isOpen,
    onOpenChange,
    currentModelId,
    itemGroups,
    onModelClick,
    canSelectModels,
    onSignInClick,
    isFreeModelsOnly,
  } = initializedProps

  const currentModelData = currentModelId ? getModelData(currentModelId) : null

  return (
    <div className={Styles['ModelPickerV2']}>
      <ActionMenu {...(onOpenChange && {open: isOpen, onOpenChange})}>
        <ActionMenu.Button className={Styles['ModelPickerV2__button']} variant="invisible">
          {currentModelData?.label || COPY.heading}
        </ActionMenu.Button>

        <ActionMenu.Overlay width="medium" align="center">
          <ActionList selectionVariant="single">
            <div className={Styles['ModelPickerV2__list-heading']}>{COPY.heading}</div>
            {itemGroups.length > 0 && <ActionList.Divider />}

            {itemGroups.map((group, index) => (
              <Fragment key={group.groupLabel}>
                <ActionList.Group key={group.groupLabel}>
                  <ActionList.GroupHeading>{group.groupLabel}</ActionList.GroupHeading>
                  {group.items.map(item => {
                    const canSelectModel = canSelectModels && (!isFreeModelsOnly || item.isFree)

                    const ModelIcon = (
                      <img
                        className={Styles['ModelPickerV2__list-itemVisual']}
                        src={`${MANUFACTURER_ICONS[item.manufacturer]}?size=36`}
                        alt={item.manufacturer}
                      />
                    )

                    return (
                      <ActionList.Item
                        key={item.id}
                        disabled={!canSelectModel}
                        selected={item.id === currentModelId}
                        onSelect={() => {
                          onModelClick(item.id)
                        }}
                      >
                        <ActionList.LeadingVisual>{ModelIcon}</ActionList.LeadingVisual>
                        {item.label}
                        {item.isPreview && (
                          <Text className={Styles['ModelPickerV2__list-previewBadge']} size="small">
                            {COPY.preview}
                          </Text>
                        )}
                      </ActionList.Item>
                    )
                  })}
                </ActionList.Group>
                {index < itemGroups.length - 1 && <ActionList.Divider />}
              </Fragment>
            ))}

            {onSignInClick && (
              <div className={Styles['ModelPickerV2__signIn']}>
                {COPY.signInCta.description && (
                  <Text className={Styles['ModelPickerV2__signIn-description']} size="medium">
                    {COPY.signInCta.description}
                  </Text>
                )}

                <Button variant="primary" block onClick={onSignInClick}>
                  {COPY.signInCta.label}
                </Button>
              </div>
            )}
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
    </div>
  )
}

export default ModelPickerV2
